import React, {
    Component
} from 'react';
import {renderToString} from 'react-dom/server'
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import _ from 'lodash';
import memoize from 'memoize-one';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {Toast} from 'primereact/toast';

import StackError from '~/shared/src/base/StackError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {ClearConfigParameters} from '~/shared/src/utilities/modules/form';
import dataExtraction from '~/shared/src/utilities/dataExtraction';

import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import Designer, {
    ReferenceType as DesignerReferenceType
} from '~/frontend/src/components/base/MetaModelDesigner';
import Form from '~/frontend/src/components/base/Form';
import Headerbar from '~/frontend/src/components/base/MetaModelDesigner/Headerbar';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Menu, {
    ReferenceType as MenuReferenceType
} from '~/frontend/src/components/base/MetaModelDesigner/DesignerTree';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Toolbar from '~/frontend/src/components/base/MetaModelDesigner/Toolbar';

import {
    isDataModified,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {getIconStringSpan} from '~/frontend/src/utilities/renderHelpers';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import * as CiActions from '~/frontend/src/actions/data';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import loadData from '~/frontend/src/actions/data/loadData';
import addItem from '~/frontend/src/actions/data/addItem';
import addReference from '~/frontend/src/actions/data/addReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type IdToCountMapping = {
    [ id : number ] : number;
};

type Variables = {
    [ variable : string ] : any;
};

export type ApplicationToYnItemTypeItemIdMapping = {
    [ name : string ] : {
        [ ynItemTypeItemId : number ] : boolean
    };
};
export type YnItemType = {
    id : number;
    key : string;
    label : string;
    icon : string;
    dirty : boolean;
    error? : {
        count : number;
        tooltip? : string;
    };
    deleted : boolean;
    deletable : boolean;
};
export type YnReferenceSet = {
    id : number;
    label : string;
    fullLabel : string;
    cardinality : ReferenceSet['cardinality'];
    sourceId : number;
    targetId? : number;
    dirty : boolean;
    error? : {
        count : number;
        tooltip? : string;
    };
    deleted : boolean;
    deletable : boolean;
};


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean;
    ynItemTypeIdToItemCountMapping : IdToCountMapping;
    ynReferenceSetIdToReferenceCountMapping : IdToCountMapping;
};


const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'metaModelDesigner' as string,
    //
    title: 'MetaModelDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    lockApplication: false as boolean,

    readOnly: true as boolean,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number
};
type DefaultProps = Required<typeof defaultProps>;

type Props = {
    uid : string;

    application? : string;

    highlightYnReferenceSetId? : number;
    selectedYnReferenceSetId? : number;

    highlightYnItemTypeId? : number;
    selectedYnItemTypeId? : number;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( dataContext : string ) => void;

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    language : string;
    messageCatalogTimestamp : number;
    metaModelTimestamp : number;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    addReference : typeof addReference;
    addItem : typeof addItem;
};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;


type State = {
    applicationToYnItemTypeItemIdMapping : ApplicationToYnItemTypeItemIdMapping;
    ynItemTypes : YnItemType[];
    ynItemTypesStringified : string;
    ynReferenceSets : YnReferenceSet[];
    ynReferenceSetsStringified : string;

    ynItemTypeItemId? : number;

    showChooseItemTypeToAddDialog : boolean;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    mobile : boolean;
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


class MetaModelDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'MetaModelDesignerView';
    static panelTabTitle = 'YN.base.frontend.metaModelDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:YnItemType';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.metaModelDesigner';
    static panelPropsSchema = Joi.object( {
        panelType      : Joi.string().valid( MetaModelDesignerView.panelType ),
        panelTabTitle  : Joi.string().default( MetaModelDesignerView.panelTabTitle ),
        panelTabIcon   : Joi.string().default( MetaModelDesignerView.panelTabIcon ),
        menuWidth      : Joi.number()
            .default( 15 )
            .description( 'The width of the left menu panel in percent of the available width.' ),
        menu2Width     : Joi.number()
            .default( 30 )
            .description( 'The width of the right menu panel in percent of the available width.' ),
        onSave         : Joi.function(),
        application    : Joi.string().optional(),
        lockApplication: Joi.boolean().optional()
    } );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
            }

            dispatch( clearElusiveDataForSubpanels( props.uid ) );
            dispatch( clearElusiveData( props.uid ) );

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await MetaModelDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await MetaModelDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'application' | 'lockApplication'> ) : string | undefined {
        const {
                  application,
                  lockApplication
              } = props;

        let retVal : string = `#${MetaModelDesignerView.panelType}`;

        if ( lockApplication && application ) {
            retVal += `/${encodeURIComponent( application )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = application
        if ( params?.[ 1 ] ) {
            retVal.application = decodeURIComponent( params[ 1 ] );
            retVal.lockApplication = true;
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  onSave,
                  selectedYnItemTypeId
              }                                                  = propsWithDefaults,
              result                                             = await dispatch( saveData(
                  uid,
                  'CFG',
                  0,
                  'metamodel',
                  'save',
                  undefined,
                  successMsgParams
              ) );

        if ( selectedYnItemTypeId && result && result.data?.itemMetaDataMapping?.[ String( selectedYnItemTypeId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedYnItemTypeId', result.data.itemMetaDataMapping[ String( selectedYnItemTypeId ) as unknown as number ].id ) );
        }

        onSave?.( uid );

        return Promise.resolve( result );
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = MetaModelDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    /**
     * Attention: unused parameters used for memoize - DO NOT DELETE!
     *
     * Extracts the mapping of applications to YnItemType items.
     */
    static getApplicationToYnItemTypeItemIdMapping = memoize( ( dataContext : AllProps['dataContext'], schema : AllProps['schema'], globalDataVersions : PanelElusiveData[ 'data' ], language : string, messageCatalogTimestamp : number, metaModelTimestamp : number ) => {
        const retVal : ApplicationToYnItemTypeItemIdMapping = {},
              applicationItemTypeId                         = metaModel[ 'CFG' ].getItemType( 'Application' )?.id ?? 0,
              ynItemTypeBelongsToApplicationReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'Application', 'belongsTo', 'src' )[ 0 ]?.id ?? 0;

        for ( const item of Object.values( global.data[ dataContext ]?.items ?? {} ) ) {
            if ( !item.isDeleted() && item.itemTypeId === applicationItemTypeId ) {
                if ( !retVal[ item.name ] ) {
                    retVal[ item.name ] = {};
                }

                for ( const referenceId of item.references ) {
                    const reference = global.data[ dataContext ].references[ referenceId ];

                    if ( reference && reference.referenceSetId === ynItemTypeBelongsToApplicationReferenceSetId ) {
                        const referencedItem = global.data[ dataContext ].items[ reference.srcItemId ];

                        retVal[ item.name ][ reference.srcItemId ] = !reference.isDeleted() && !referencedItem?.isDeleted();
                    }
                }
            }
        }

        return retVal;
    } );

    /**
     * Attention: unused parameters used for memoize - DO NOT DELETE!
     *
     * Extracts the list of ItemTypes, but not as real ItemType objects.
     */
    static getYnItemTypes = memoize( ( ynItemTypeIdToItemCountMapping : IdToCountMapping | undefined, dataContext : AllProps['dataContext'], schema : AllProps['schema'], globalDataVersions : PanelElusiveData[ 'data' ], language : string, messageCatalogTimestamp : number, metaModelTimestamp : number ) => {
        const retVal : YnItemType[]                                  = [],
              ynItemTypeItemTypeId                                   = metaModel[ 'CFG' ].getItemType( 'YnItemType' )?.id,
              ynItemTypeIsDestObjectTypeYnReferenceSetReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isDestObjectType', 'src' )[ 0 ]?.id ?? 0,
              ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isSourceObjectType', 'src' )[ 0 ]?.id ?? 0,
              imageIsIconForYnItemTypeReferenceSetId                 = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Image', 'YnItemType', 'isIconFor', 'src' )[ 0 ]?.id,
              messageIsTypeLabelForYnItemTypeReferenceSetId          = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnItemType', 'isTypeLabelFor', 'src' )[ 0 ]?.id;

        for ( const item of Object.values( global.data[ dataContext ]?.items ?? {} ) ) {
            if ( item.itemTypeId === ynItemTypeItemTypeId ) {
                let label : YnItemType['label'] = item.name,
                    icon : string               = getIconStringSpan( 'p-treenode-icon yn-tree-icon icon-itemtype-detail fa fa-shapes' ),
                    isDirty                     = item.isModified();

                for ( const referenceId of item.references ) {
                    const reference = global.data[ dataContext ].references[ referenceId ];

                    if ( reference && !reference.isDeleted() ) {
                        const referencedItem = reference?.srcItemId === item.id
                            ? global.data[ dataContext ].items[ reference.destItemId ]
                            : global.data[ dataContext ].items[ reference.srcItemId ];

                        isDirty = isDirty || reference.isModified() || referencedItem?.isModified();

                        if ( reference.referenceSetId === ynItemTypeIsDestObjectTypeYnReferenceSetReferenceSetId ) {
                            for ( const subReferenceId of referencedItem?.references ?? [] ) {
                                const subReference = global.data[ dataContext ].references[ subReferenceId ];

                                if ( subReference && subReference.referenceSetId === ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId ) {
                                    const subReferencedItem = subReference?.srcItemId === referencedItem.id
                                        ? global.data[ dataContext ].items[ subReference.destItemId ]
                                        : global.data[ dataContext ].items[ subReference.srcItemId ];

                                    isDirty = isDirty || subReference.isModified() || subReferencedItem?.isModified();
                                }
                            }
                        }
                        else if ( reference.referenceSetId === ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId ) {
                            for ( const subReferenceId of referencedItem?.references ?? [] ) {
                                const subReference = global.data[ dataContext ].references[ subReferenceId ];

                                if ( subReference && subReference.referenceSetId === ynItemTypeIsDestObjectTypeYnReferenceSetReferenceSetId ) {
                                    const subReferencedItem = subReference?.srcItemId === referencedItem.id
                                        ? global.data[ dataContext ].items[ subReference.destItemId ]
                                        : global.data[ dataContext ].items[ subReference.srcItemId ];

                                    isDirty = isDirty || subReference.isModified() || subReferencedItem?.isModified();
                                }
                            }
                        }
                        else if ( reference.referenceSetId === messageIsTypeLabelForYnItemTypeReferenceSetId ) {
                            const messageItem = global.data[ dataContext ].items[ reference.srcItemId ];

                            if ( !messageItem?.isDeleted() ) {
                                const properties = dataExtraction.extractPropertiesOfItem( messageItem );

                                if ( typeof properties.content === 'object' ) {
                                    label = __( `{${Object.entries( properties.content ).map( e => `${e[ 0 ]}: ${e[ 1 ]}` ).join( '}, {' )}}` );
                                }
                            }
                        }
                        else if ( reference.referenceSetId === imageIsIconForYnItemTypeReferenceSetId ) {
                            const imageItem = global.data[ dataContext ].items[ reference.srcItemId ];

                            if ( !imageItem?.isDeleted() ) {
                                const properties = dataExtraction.extractPropertiesOfItem( imageItem );

                                let style : string = '';

                                if ( properties.content ) {
                                    const image = document.createElement( 'img' );

                                    image.src = properties.content[ '2x' ] ?? properties.content[ '1x' ];

                                    style = `background-size : ${image.width}px ${image.height}px; background-image: url(${properties.content[ '2x' ] ?? properties.content[ '1x' ]});`;
                                }

                                icon = getIconStringSpan( `p-treenode-icon yn-tree-icon icon-itemtype-detail${properties.cssClass ? ` ${properties.cssClass}` : ''}`, { style } );
                            }
                        }
                    }
                }

                retVal.push( {
                    id       : item.id,
                    key      : item.key,
                    label,
                    icon     : icon,
                    dirty    : isDirty,
                    deleted  : item.isDeleted(),
                    deletable: !ynItemTypeIdToItemCountMapping?.[ item.id ]
                } );
            }
        }

        return retVal;
    } );

    static getYnReferenceSets = memoize( ( ynReferenceSetIdToReferenceCountMapping : IdToCountMapping | undefined, ynItemTypes : YnItemType[], dataContext : AllProps['dataContext'], schema : AllProps['schema'], globalDataVersions : PanelElusiveData[ 'data' ], language : string, messageCatalogTimestamp : number, metaModelTimestamp : number ) => {
        const retVal : YnReferenceSet[]                              = [],
              ynReferenceSetItemTypeId                               = metaModel[ 'CFG' ].getItemType( 'YnReferenceSet' )?.id,
              ynItemTypeIsDestObjectTypeYnReferenceSetReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isDestObjectType', 'src' )[ 0 ]?.id ?? 0,
              ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isSourceObjectType', 'src' )[ 0 ]?.id ?? 0,
              messageIsTypeLabelForYnReferenceSetReferenceSetId      = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Message', 'YnReferenceSet', 'isTypeLabelFor', 'src' )[ 0 ]?.id;

        for ( const item of Object.values( global.data[ dataContext ]?.items ?? {} ) ) {
            if ( item.itemTypeId === ynReferenceSetItemTypeId ) {
                const referenceSetProperties = dataExtraction.extractPropertiesOfItem( item );

                let isDeleted                               = item.isDeleted(),
                    isDeletable                             = !ynReferenceSetIdToReferenceCountMapping?.[ item.id ],
                    srcItemTypeItemId : number | undefined  = undefined,
                    destItemTypeItemId : number | undefined = undefined;

                for ( const referenceId of item.references ) {
                    const reference = global.data[ dataContext ].references[ referenceId ];

                    if ( reference && !reference.isDeleted() ) {
                        if ( reference && !reference.isDeleted() && reference.referenceSetId === ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId ) {
                            srcItemTypeItemId = global.data[ dataContext ].items[ reference.srcItemId ] ? reference.srcItemId : undefined;
                        }
                        else if ( reference && !reference.isDeleted() && reference.referenceSetId === ynItemTypeIsDestObjectTypeYnReferenceSetReferenceSetId ) {
                            destItemTypeItemId = global.data[ dataContext ].items[ reference.srcItemId ] ? reference.srcItemId : undefined;
                        }
                    }
                }

                if (
                    srcItemTypeItemId
                    && ( item.isNew() || destItemTypeItemId )
                ) {
                    let label              = '',
                        referenceTypeLabel = referenceSetProperties.referenceTypeName ?? '';

                    isDeleted = isDeleted || global.data[ dataContext ].items[ srcItemTypeItemId ]?.isDeleted();
                    if ( destItemTypeItemId ) {
                        isDeleted = isDeleted || global.data[ dataContext ].items[ destItemTypeItemId ]?.isDeleted();
                    }

                    isDeletable = isDeletable && !global.data[ dataContext ].items[ srcItemTypeItemId ]?.isDeleted();
                    if ( destItemTypeItemId ) {
                        isDeletable = isDeletable && !global.data[ dataContext ].items[ destItemTypeItemId ]?.isDeleted();
                    }

                    for ( const referenceId of item.references ) {
                        const reference = global.data[ dataContext ].references[ referenceId ];

                        if ( reference && !reference.isDeleted() && reference.referenceSetId === messageIsTypeLabelForYnReferenceSetReferenceSetId ) {
                            const messageItem = global.data[ dataContext ].items[ reference.srcItemId ];

                            if ( !messageItem?.isDeleted() ) {
                                const properties = dataExtraction.extractPropertiesOfItem( messageItem );

                                if ( typeof properties.content === 'object' ) {
                                    referenceTypeLabel = __( `{${Object.entries( properties.content ).map( e => `${e[ 0 ]}: ${e[ 1 ]}` ).join( '}, {' )}}` );
                                }
                            }
                        }
                    }

                    label += ynItemTypes.find( ynItemType => ynItemType.id === srcItemTypeItemId )?.label ?? '';
                    label += ` ${__( 'YN.base.common.reference.directionMarker' )} `;
                    if ( referenceTypeLabel ) {
                        label += referenceTypeLabel;
                        label += ` ${__( 'YN.base.common.reference.directionMarker' )} `;

                        if ( destItemTypeItemId ) {
                            label += ynItemTypes.find( ynItemType => ynItemType.id === destItemTypeItemId )?.label ?? '';
                        }
                    }

                    retVal.push( {
                        id         : item.id,
                        label      : referenceTypeLabel,
                        fullLabel  : label,
                        cardinality: referenceSetProperties.cardinality,
                        sourceId   : srcItemTypeItemId,
                        targetId   : destItemTypeItemId,
                        dirty      : item.isModified(),
                        deleted    : isDeleted,
                        deletable  : isDeletable
                    } );
                }
            }
        }

        return retVal;
    } );

    mediaQuery = matchMedia( 'screen and (max-width: 960px)' );
    menuContentPanelContentStyle = { padding: 0 };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    menu : MenuReferenceType | null = null;
    designer : DesignerReferenceType | null = null;

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        const applicationToYnItemTypeItemIdMapping = MetaModelDesignerView.getApplicationToYnItemTypeItemIdMapping(
                  props.dataContext,
                  props.schema,
                  props.allElusiveData?.[ props.dataContext ]?.data,
                  props.language,
                  props.messageCatalogTimestamp,
                  props.metaModelTimestamp
              ),
              ynItemTypes                          = MetaModelDesignerView.getYnItemTypes(
                  undefined,
                  props.dataContext,
                  props.schema,
                  props.allElusiveData?.[ props.dataContext ]?.data,
                  props.language,
                  props.messageCatalogTimestamp,
                  props.metaModelTimestamp
              ),
              ynReferenceSets                      = MetaModelDesignerView.getYnReferenceSets(
                  undefined,
                  ynItemTypes,
                  props.dataContext,
                  props.schema,
                  props.allElusiveData?.[ props.dataContext ]?.data,
                  props.language,
                  props.messageCatalogTimestamp,
                  props.metaModelTimestamp
              );

        this.state = {
            applicationToYnItemTypeItemIdMapping,
            ynItemTypes,
            ynItemTypesStringified    : JSON.stringify( ynItemTypes ),
            ynReferenceSets,
            ynReferenceSetsStringified: JSON.stringify( ynReferenceSets ),

            showChooseItemTypeToAddDialog: false,

            isDirty   : false,
            errorCount: 0,

            scrollIndicator: 0 as number,

            initialized: false,

            mobile: getMobile()
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getDesigner = memoize( this.getDesigner, this.getDesignerEqual );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar, this.getToolbarEqual );
        this.getMenu = memoize( this.getMenu, this.getMenuEqual );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onAddButtonClick = this.onAddButtonClick.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectApplication = this.onSelectApplication.bind( this );
        this.onHighlightYnItemTypeId = this.onHighlightYnItemTypeId.bind( this );
        this.onSelectYnItemTypeId = this.onSelectYnItemTypeId.bind( this );
        this.onHighlightYnReferenceSetId = this.onHighlightYnReferenceSetId.bind( this );
        this.onSelectYnReferenceSetId = this.onSelectYnReferenceSetId.bind( this );
        this.onChooseItemTypeToAddDialogOkButtonClick = this.onChooseItemTypeToAddDialogOkButtonClick.bind( this );
        this.onChooseItemTypeToAddDialogHide = this.onChooseItemTypeToAddDialogHide.bind( this );
        this.onResetFormData = this.onResetFormData.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const retVal : Partial<State>                        = {},
              {
                  uid,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              {
                  showChooseItemTypeToAddDialog
              }                                              = state,
              ynItemTypes                                    = MetaModelDesignerView.getYnItemTypes(
                  props.elusiveData?.ynItemTypeIdToItemCountMapping,
                  props.dataContext,
                  props.schema,
                  props.allElusiveData?.[ props.dataContext ]?.data,
                  props.language,
                  props.messageCatalogTimestamp,
                  props.metaModelTimestamp
              ),
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        retVal.applicationToYnItemTypeItemIdMapping = MetaModelDesignerView.getApplicationToYnItemTypeItemIdMapping(
            props.dataContext,
            props.schema,
            props.allElusiveData?.[ props.dataContext ]?.data,
            props.language,
            props.messageCatalogTimestamp,
            props.metaModelTimestamp
        );

        if ( !showChooseItemTypeToAddDialog ) {
            retVal.ynItemTypeItemId = undefined;
        }

        retVal.isDirty = isDataModified( dataContext, 'CFG' );
        retVal.errorCount = 0;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        retVal.isDirty = retVal.isDirty || !!panelElusiveData.isDirty;

                        if ( panelElusiveData.errorData?.count ) {
                            const formItemId = Number( panelKey.replace( /^form-/, '' ) ),
                                  ynItemType = ynItemTypes.find( yIT => yIT.id === formItemId );

                            retVal.errorCount += panelElusiveData.errorData.count;

                            if ( ynItemType && !ynItemType.deleted ) {
                                ynItemType.error = {
                                    count  : panelElusiveData.errorData.count,
                                    tooltip: panelElusiveData.errorData.tooltip?.length ? renderToString( <>{panelElusiveData.errorData.tooltip}</> ) : undefined
                                };
                            }

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.metaModelDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.metaModelDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        const ynItemTypesStringified = JSON.stringify( ynItemTypes );
        if ( ynItemTypesStringified !== state.ynItemTypesStringified ) {
            retVal.ynItemTypes = JSON.parse( ynItemTypesStringified );
            retVal.ynItemTypesStringified = ynItemTypesStringified;
        }

        const ynReferenceSets = MetaModelDesignerView.getYnReferenceSets(
            props.elusiveData?.ynReferenceSetIdToReferenceCountMapping,
            retVal.ynItemTypes ?? state.ynItemTypes,
            props.dataContext,
            props.schema,
            props.allElusiveData?.[ props.dataContext ]?.data,
            props.language,
            props.messageCatalogTimestamp,
            props.metaModelTimestamp
        );

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 && panelElusiveData.errorData?.count ) {
                        const formItemId     = Number( panelKey.replace( /^form-/, '' ) ),
                              ynReferenceSet = ynReferenceSets.find( yRS => yRS.id === formItemId );

                        if ( ynReferenceSet && !ynReferenceSet.deleted ) {
                            ynReferenceSet.error = {
                                count  : panelElusiveData.errorData.count,
                                tooltip: panelElusiveData.errorData.tooltip?.length ? renderToString( <>{panelElusiveData.errorData.tooltip}</> ) : undefined
                            };
                        }
                    }
                }
            }
        }

        const ynReferenceSetsStringified = JSON.stringify( ynReferenceSets );
        if ( ynReferenceSetsStringified !== state.ynReferenceSetsStringified ) {
            retVal.ynReferenceSets = JSON.parse( ynReferenceSetsStringified );
            retVal.ynReferenceSetsStringified = ynReferenceSetsStringified;
        }

        if ( retVal.errorCount ) {
            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.common.tooltip.error.title' )}
                </div>
                {tooltipErrors}
            </div>;
            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {messageErrors}
                </div>
            </>;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.mediaQuery.addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        if ( !this.props.elusiveData ) {
            void this.loadData();
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        this.mediaQuery.removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        const {
                  uid
              } = this.props,
              {
                  isDirty
              } = this.state;

        this.afterRender();

        // mark panel dirty
        if ( isDirty !== undefined && isDirty !== prevState.isDirty ) {
            this.props.setPanelDirty( uid, isDirty );
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  schema,
                  uid
              }      = this.props,

              retVal = await this.props.loadData?.(
                  'metamodel/loadConfigData',
                  dataContext,
                  uid,
                  'loadConfigData',
                  'CFG',
                  {
                      params   : {
                          schema
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      this.props.setElusiveData( uid, {
                          ynItemTypeIdToItemCountMapping         : resonseData.data.ynItemTypeIdToItemCountMapping,
                          ynReferenceSetIdToReferenceCountMapping: resonseData.data.ynReferenceSetIdToReferenceCountMapping
                      } );

                      this.setState( {
                          initialized: true
                      } );
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  application,
                  intercomMessages,
                  lockApplication
              }     = props,
              {
                  isDirty,
                  errorCount
              }     = this.state,
              icon  = this.getPanelIcon( MetaModelDesignerView.panelTabIcon ),
              title = this.getPanelTitle( MetaModelDesignerView.panelTabTitle ),
              path  = MetaModelDesignerView.buildPanelPathFromProps( { application, lockApplication } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    addItemType( ynItemTypeItemId? : number, application? : string ) {
        const {
                  dataContext,
                  schema,
                  uid
              }                                            = this.props,
              activeStatusId                               = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
              ynItemTypeItemTypeId                         = metaModel[ 'CFG' ].getItemType( 'YnItemType' )?.id,
              ynItemTypeModulePropertySetId                = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnItemType', 'Module' )?.id,
              ynItemTypeOriginPropertySetId                = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnItemType', 'Origin' )?.id,
              ynItemTypeSchemaPropertySetId                = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnItemType', 'Schema' )?.id,
              ynItemTypeBelongsToApplicationReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'Application', 'belongsTo', 'src' )[ 0 ]?.id,
              applicationItem                              = Object.values( global.data[ dataContext ]?.items ?? {} ).find( item => item.name === application );

        if ( ynItemTypeItemTypeId && ynItemTypeModulePropertySetId && ynItemTypeOriginPropertySetId && ynItemTypeSchemaPropertySetId && !ynItemTypeItemId ) {
            const ynItemTypeItem = this.props.addItem(
                dataContext,
                {
                    itemTypeId: ynItemTypeItemTypeId,
                    statusId  : activeStatusId,
                    properties: [
                        {
                            propertySetId: ynItemTypeModulePropertySetId,
                            value        : 'base',
                            statusId     : activeStatusId
                        },
                        {
                            propertySetId: ynItemTypeOriginPropertySetId,
                            value        : global.config.common.uuidOrigin,
                            statusId     : activeStatusId
                        },
                        {
                            propertySetId: ynItemTypeSchemaPropertySetId,
                            value        : schema.toLowerCase(),
                            statusId     : activeStatusId
                        }
                    ]
                },
                'CFG'
            );

            if ( ynItemTypeItem ) {
                ynItemTypeItemId = ynItemTypeItem.id;
            }
        }

        if ( ynItemTypeItemId && applicationItem && ynItemTypeBelongsToApplicationReferenceSetId ) {
            this.props.addReference(
                dataContext,
                {
                    referenceSetId: ynItemTypeBelongsToApplicationReferenceSetId,
                    statusId      : activeStatusId,
                    srcItemId     : ynItemTypeItemId,
                    destItemId    : applicationItem.id
                },
                'CFG'
            );
        }

        this.props.setParam( uid, 'selectedYnItemTypeId', ynItemTypeItemId );
    }

    onAddButtonClick() {
        const {
                  application
              } = this.props,
              {
                  applicationToYnItemTypeItemIdMapping,
                  ynItemTypes
              } = this.state;

        // show dialog for choosing to add an existing ItemType or a new one
        if ( application && Object.keys( applicationToYnItemTypeItemIdMapping[ application ] ?? {} ).length < ynItemTypes.length ) {
            this.setState( { showChooseItemTypeToAddDialog: true } );
        }
        else {
            this.addItemType( undefined, application );
        }
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext ].schema
            };

            this.setState( {}, () => void this.loadData() );
        }
    }

    onResetFormData( itemId : number ) {
        const {
                  allElusiveData,
                  uid
              } = this.props;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelKey = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const formItemId = Number( panelKey.replace( /^form-/, '' ) );

                        if ( formItemId === itemId ) {
                            this.props.clearElusiveData( elusiveDataPanelKey );
                        }
                    }
                }
            }
        }
    }

    onMenuScroll() {
        this.setState( prevState => ( { scrollIndicator: ( prevState.scrollIndicator ?? 0 ) + 1 } ) );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onSelectApplication( application? : string ) {
        this.props.setParam?.( this.props.uid, 'application', application );
    }

    onHighlightYnReferenceSetId( highlightYnReferenceSetId? : number ) {
        this.props.setParam?.( this.props.uid, 'highlightYnReferenceSetId', highlightYnReferenceSetId );
    }

    onSelectYnReferenceSetId( selectedYnReferenceSetId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedYnReferenceSetId', selectedYnReferenceSetId );
    }

    onHighlightYnItemTypeId( highlightYnItemTypeId? : number ) {
        this.props.setParam?.( this.props.uid, 'highlightYnItemTypeId', highlightYnItemTypeId );
    }

    onSelectYnItemTypeId( selectedYnItemTypeId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedYnItemTypeId', selectedYnItemTypeId );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    onChooseItemTypeToAddDialogOkButtonClick( ynItemTypeItemId? : number ) {
        const {
                  application
              } = this.props;

        this.addItemType( ynItemTypeItemId, application );
        this.setState( { showChooseItemTypeToAddDialog: false } );
    }

    onChooseItemTypeToAddDialogHide() {
        this.setState( { showChooseItemTypeToAddDialog: false } );
    }

    getHeaderbar( uid : string, lockApplication : boolean, application? : string ) {
        return <Headerbar
            key={uid}

            application={application}
            lockApplication={lockApplication}
        />;
    }

    getToolbar( uid : string, dataContext : string, schema : Schema, readOnly : boolean, applicationToYnItemTypeItemIdMapping : ApplicationToYnItemTypeItemIdMapping, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[], isDirty : boolean, application? : string ) {
        return <Toolbar
            key={uid}

            uid={uid}

            dataContext={dataContext}
            schema={schema}
            application={application}
            applicationToYnItemTypeItemIdMapping={applicationToYnItemTypeItemIdMapping}

            addButtonDisabled={readOnly}
            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}

            onReloadButtonClick={this.onReloadButtonClick}
            onAddButtonClick={this.onAddButtonClick}

            showModifiedHint={isDirty}
        />;
    }

    getToolbarEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getMenu( uid : string, dataContext : string, schema : Schema, treeProps : any, readOnly : boolean, additionalElusiveDataContexts : string[], applicationToYnItemTypeItemIdMapping : ApplicationToYnItemTypeItemIdMapping, ynItemTypes : YnItemType[], ynReferenceSets : YnReferenceSet[], lockApplication : boolean, application? : string, highlightYnItemTypeId? : number, selectedYnItemTypeId? : number, highlightYnReferenceSetId? : number, selectedYnReferenceSetId? : number ) {
        return <Menu
            ref={( el ) => this.menu = el}

            {...treeProps}

            uid={uid}

            schema={schema}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}

            readOnly={readOnly}

            applicationToYnItemTypeItemIdMapping={applicationToYnItemTypeItemIdMapping}
            ynItemTypes={ynItemTypes}
            ynReferenceSets={ynReferenceSets}
            application={application}
            showApplicationDropdown={!lockApplication}
            onSelectApplication={this.onSelectApplication}

            highlightYnItemTypeId={highlightYnItemTypeId}
            onHighlightYnItemTypeId={this.onHighlightYnItemTypeId}

            selectedYnItemTypeId={selectedYnItemTypeId}
            onSelectYnItemTypeId={this.onSelectYnItemTypeId}

            highlightYnReferenceSetId={highlightYnReferenceSetId}
            onHighlightYnReferenceSetId={this.onHighlightYnReferenceSetId}

            selectedYnReferenceSetId={selectedYnReferenceSetId}
            onSelectYnReferenceSetId={this.onSelectYnReferenceSetId}

            onResetFormData={this.onResetFormData}
        />;
    }

    getMenuEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getDesigner( uid : string, dataContext : string, schema : Schema, designerProps : any, additionalElusiveDataContexts : string[], applicationToYnItemTypeItemIdMapping : ApplicationToYnItemTypeItemIdMapping, ynItemTypes : YnItemType[], ynReferenceSets : YnReferenceSet[], application? : string, highlightYnItemTypeId? : number, selectedYnItemTypeId? : number, highlightYnReferenceSetId? : number, selectedYnReferenceSetId? : number ) {
        return <Designer
            ref={( el ) => this.designer = el}

            {...designerProps}

            uid={uid}

            schema={schema}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}

            applicationToYnItemTypeItemIdMapping={applicationToYnItemTypeItemIdMapping}
            ynItemTypes={ynItemTypes}
            ynReferenceSets={ynReferenceSets}
            application={application}

            highlightYnItemTypeId={highlightYnItemTypeId}
            onHighlightYnItemTypeId={this.onHighlightYnItemTypeId}

            selectedYnItemTypeId={selectedYnItemTypeId}
            onSelectYnItemTypeId={this.onSelectYnItemTypeId}

            highlightYnReferenceSetId={highlightYnReferenceSetId}
            onHighlightYnReferenceSetId={this.onHighlightYnReferenceSetId}

            selectedYnReferenceSetId={selectedYnReferenceSetId}
            onSelectYnReferenceSetId={this.onSelectYnReferenceSetId}
        />;
    }

    getDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, readOnly : boolean, treeVariables : Variables ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            readOnly={readOnly}
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeVariables={treeVariables}
        />
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getChooseItemTypeToAddDialogDialog() {
        const {
                  application,
                  uid
              }                           = this.props,
              {
                  applicationToYnItemTypeItemIdMapping,
                  ynItemTypes,
                  showChooseItemTypeToAddDialog,
                  ynItemTypeItemId
              }                           = this.state,
              style : React.CSSProperties = {
                  width    : '33rem',
                  minWidth : '33rem',
                  minHeight: '3rem'
              },
              filteredYnItemTypeItemIds   = Object.keys( applicationToYnItemTypeItemIdMapping[ application ?? '' ] ?? {} ).map( i => Number( i ) ),
              options                     = ynItemTypes.reduce(
                  ( acc, itemType ) => {
                      if ( !application || !filteredYnItemTypeItemIds.includes( itemType.id ) ) {
                          acc.push( {
                              value: itemType.id,
                              label: __( 'YN.base.frontend.metaModelDesigner.chooseItemTypeToAddDialog.option.label', { itemType: itemType.label } )
                          } );
                      }

                      return acc;
                  },
                  [] as { label : string, value : number }[]
              );

        return <Dialog
            key={`${uid}_createNewChartDialog`}
            id={`${uid}_createNewChartDialog`}

            visible={showChooseItemTypeToAddDialog}
            onHide={this.onChooseItemTypeToAddDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-form-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.metaModelDesigner.chooseItemTypeToAddDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    onClick={() => {
                        this.onChooseItemTypeToAddDialogOkButtonClick( ynItemTypeItemId );
                    }}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onChooseItemTypeToAddDialogHide}
                />
            </div>}
        >
            {__( 'YN.base.frontend.metaModelDesigner.chooseItemTypeToAddDialog.text' )}
            <Dropdown
                value={ynItemTypeItemId}
                options={options}
                onChange={( e : any ) => {
                    this.setState( { ynItemTypeItemId: e.value } );
                }}
                className="yn-application-dropdown"
                style={{ width: '100%', marginTop: '1rem', marginBottom: '1rem' }}
                placeholder={__( 'YN.base.frontend.metaModelDesigner.chooseItemTypeToAddDialog.placeholder' )}
            />
        </Dialog>;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  application,
                  dataContext,
                  elusiveData,
                  highlightYnItemTypeId,
                  highlightYnReferenceSetId,
                  lockApplication,
                  menu2Width,
                  menuWidth,
                  schema,
                  selectedYnItemTypeId,
                  selectedYnReferenceSetId,
                  subPanelProps,
                  uid,
                  user
              }                 = this.props,
              {
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  isDirty,
                  showChooseItemTypeToAddDialog,
                  applicationToYnItemTypeItemIdMapping,
                  ynItemTypes,
                  ynReferenceSets
              }                 = this.state,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              readOnly          = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) ) || !( user?.isMemberOfGroup( config.sysGroups?.metaModelAdmin ?? '' ) ),
              currentItem       = selectedYnItemTypeId
                  ? global.data[ dataContext ]?.items[ selectedYnItemTypeId ]
                  : selectedYnReferenceSetId
                      ? global.data[ dataContext ]?.items[ selectedYnReferenceSetId ]
                      : undefined,
              currentItemId     = currentItem?.id,
              panelConfigs      = user && currentItem && !currentItem.isDeleted() ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[] : undefined,
              panelConfig       = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^MMdSp_/ ) ) ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              headerbarUid      = 'headerbar',
              fullHeaderbarUid  = `${uid}_${headerbarUid}`,
              menuUid           = `tree`,
              fullMenuUid       = `${uid}_${menuUid}`,
              designerUid       = `designer`,
              fullDesignerUid   = `${uid}_${designerUid}`,
              formUid           = `form-${currentItemId}`,
              fullFormUid       = `${uid}_${formUid}`,
              attributesUid     = `attributes-${currentItemId}`,
              fullAttributesUid = `${uid}_${attributesUid}`,
              //
              headerbar         = this.getHeaderbar(
                  fullHeaderbarUid,
                  lockApplication,
                  application
              ),
              toolbar           = this.getToolbar(
                  uid,
                  dataContext,
                  schema,
                  readOnly,
                  applicationToYnItemTypeItemIdMapping,
                  errorCount,
                  errorTooltip,
                  isDirty,
                  application
              ),
              menu              = initialized
                  ? this.getMenu(
                      fullMenuUid,
                      dataContext,
                      schema,
                      subPanelProps?.[ menuUid ],
                      readOnly,
                      [ fullDesignerUid, fullFormUid, uid ],
                      applicationToYnItemTypeItemIdMapping,
                      ynItemTypes,
                      ynReferenceSets,
                      lockApplication,
                      application,
                      highlightYnItemTypeId,
                      selectedYnItemTypeId,
                      highlightYnReferenceSetId,
                      selectedYnReferenceSetId
                  )
                  : <></>,
              designer          = initialized
                  ? this.getDesigner(
                      fullDesignerUid,
                      dataContext,
                      schema,
                      subPanelProps?.[ designerUid ],
                      [ fullMenuUid, fullFormUid, uid ],
                      applicationToYnItemTypeItemIdMapping,
                      ynItemTypes,
                      ynReferenceSets,
                      application,
                      highlightYnItemTypeId,
                      selectedYnItemTypeId,
                      highlightYnReferenceSetId,
                      selectedYnReferenceSetId
                  )
                  : undefined,
              form              = initialized && panelConfig?.panelType === 'Form' && currentItemId
                  ? this.getForm(
                      fullFormUid,
                      dataContext,
                      subPanelProps?.[ formUid ],
                      [ fullMenuUid, fullDesignerUid, uid ],
                      panelConfig.viewKey,
                      currentItemId,
                      readOnly,
                      {
                          itemTypes    : ynItemTypes,
                          referenceSets: ynReferenceSets
                      }
                  )
                  : undefined,
              genericForm       = initialized && panelConfig?.panelType !== 'Form' && currentItem
                  ? <div key={`${uid}_formview`}>
                      <AttributesForm
                          key={fullAttributesUid}

                          {...subPanelProps?.[ attributesUid ]}

                          uid={fullAttributesUid}
                          dataContext={dataContext}
                          style={{ padding: 0 }}
                          schema="CFG"
                          status={status}
                          timestamp={timestamp}
                          timezone={timezone}
                          item={currentItem}
                          editable
                      />
                  </div>
                  : undefined;

        return <>
            {showChooseItemTypeToAddDialog &&
                this.getChooseItemTypeToAddDialogDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {headerbar}
            {toolbar}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    menu={menu}
                    menuStyle={{ display: 'flex', flexDirection: 'column' }}
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName={`yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel`}
                            onMenuWidthChange={this.onMenu2WidthChange}

                            content={<LoadingMaskPanel forComponent={fullDesignerUid}
                                                       className="yn-flex-layout-parent yn-content-panel-container p-0"
                                                       style={{ position: 'absolute', width: '100% !important', height: 'calc(100% + .5rem) !important' }}>
                                {designer}
                            </LoadingMaskPanel>}
                            menu={initialized
                                ? <LoadingMaskPanel
                                    key={`${uid}_formview_loadingmask`}
                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                    className=" yn-flex-layout-parent"
                                >
                                    <div>
                                        {form}
                                        {genericForm}
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;
    retVal.messageCatalogTimestamp = state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0
    retVal.metaModelTimestamp = state.metamodel?.timestamp ? state.metamodel.timestamp : 0
    retVal.language = state.session?.user ? state.session.user.language : '';

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ] as PanelElusiveData;
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( MetaModelDesignerView );
